import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import OptimizedImage from "./OptimizedImage";
import { resolveTrikotImagePath } from "../utils/imagePath";

const TrikotCard = ({ trikot, onBid, isLoggedIn }) => {
  const [timeLeft, setTimeLeft] = useState("");
  const [isExpired, setIsExpired] = useState(false);
  const [currentBid, setCurrentBid] = useState(trikot.currentBid);
  const [currentHighestBidder, setCurrentHighestBidder] = useState(
    trikot.highestBidder
  );
  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    setCurrentBid(trikot.currentBid);
    setCurrentHighestBidder(trikot.highestBidder);
  }, [trikot.currentBid, trikot.highestBidder]);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const endTime = new Date(trikot.endTime);
      const difference = endTime - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft("Auktion beendet");
        setRemainingTime(0);
        return;
      }

      setRemainingTime(difference);

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / 1000 / 60) % 60);
      const seconds = Math.floor((difference / 1000) % 60);

      setTimeLeft(
        `${days > 0 ? `${days}d ` : ""}${hours}h ${minutes}m ${seconds}s`
      );
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [trikot.endTime]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const renderActionButton = () => {
    if (isExpired) {
      return (
        <Button
          variant="contained"
          fullWidth
          disabled
          sx={{
            backgroundColor: "#9e9e9e",
            "&:hover": {
              backgroundColor: "#9e9e9e",
            },
          }}
        >
          Auktion beendet
        </Button>
      );
    }

    if (!isLoggedIn) {
      return (
        <Button
          component={RouterLink}
          to="/login"
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: "#1a237e",
            "&:hover": {
              backgroundColor: "#0d47a1",
            },
          }}
        >
          Login zum Bieten
        </Button>
      );
    }

    return (
      <Button
        variant="contained"
        color="secondary"
        fullWidth
        onClick={() => !isExpired && onBid(trikot)}
        sx={{
          color: "#00182c",
          fontWeight: "bold",
          "&:hover": {
            backgroundColor: "secondary.light",
            color: "#00182c",
          },
        }}
      >
        Gebot abgeben
      </Button>
    );
  };

  // Get image URLs with fallback handling
  const getImageUrls = () => {
    let webpUrl, jpegUrl;
    
    if (trikot.imageUrls && (trikot.imageUrls.webp || trikot.imageUrls.jpeg)) {
      webpUrl = trikot.imageUrls.webp || trikot.imageUrl;
      jpegUrl = trikot.imageUrls.jpeg || trikot.imageUrl;
    } else if (trikot.imageUrl) {
      // Fallback to legacy imageUrl
      webpUrl = trikot.imageUrl;
      jpegUrl = trikot.imageUrl;
    } else if (trikot.originalImageUrl) {
      // Fallback to originalImageUrl
      webpUrl = trikot.originalImageUrl;
      jpegUrl = trikot.originalImageUrl;
    } else {
      // No image available
      return {
        webp: null,
        jpeg: null,
      };
    }
    
    // Resolve paths (handles both absolute URLs and relative paths)
    return {
      webp: webpUrl ? resolveTrikotImagePath(webpUrl) : null,
      jpeg: jpegUrl ? resolveTrikotImagePath(jpegUrl) : null,
    };
  };

  const imageUrls = getImageUrls();

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transform: "translate3d(0, 0, 0)",
        transition: "transform 0.3s ease-in-out",
        "&:hover": {
          transform: "translateY(-5px)",
        },
      }}
    >
      <CardMedia
        component="div"
        sx={{
          height: "100%",
          backgroundColor: "#f5f5f5",
          marginBottom: 0,
          minHeight: "300px", // Add minimum height for consistency
          position: "relative", // For error handling
        }}
      >
        {imageUrls.webp || imageUrls.jpeg ? (
          <OptimizedImage
            webpSrc={imageUrls.webp}
            fallbackSrc={imageUrls.jpeg}
            alt={`Trikot ${trikot.playerName}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            onError={(e) => {
              // If image fails to load, show a placeholder or default image
              console.error('Bild konnte nicht geladen werden:', e.target.src);
              e.target.onerror = null; // Prevent infinite loop
              e.target.src = "/assets/img/placeholder-trikot.jpg"; // Add a placeholder image
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              color: "#999",
            }}
          >
            <Typography variant="body2">Kein Bild verfügbar</Typography>
          </Box>
        )}
      </CardMedia>

      <CardContent
        sx={{
          flexGrow: 1,
          padding: 0,
          "&:last-child": { paddingBottom: 0 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "25% 75%",
            gap: 0,
            mb: 0,
            width: "100%",
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgb(113, 198, 255)",
              backgroundImage: "linear-gradient(116deg, rgb(211, 236, 255) 0%, rgb(142, 210, 255) 100%)",
              backgroundBlendMode: "normal",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 1,
            }}
          >
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: "500",
                fontSize: "3.0rem",
                color: "rgb(0, 36, 79)",
                textAlign: "center",
                fontFamily: "countach, authority-italic, sans-serif",
              }}
            >
              {trikot.number}
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: "rgb(0, 17, 40)",
              backgroundImage: "linear-gradient(42deg, rgb(0, 17, 40) 0%, rgb(0, 36, 79) 100%)",
              backgroundBlendMode: "normal",
              padding: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {trikot.playerName.split(" ").map((namePart, index) => (
              <Typography
                key={index}
                variant="h5"
                component="div"
                sx={{
                  fontWeight: "500",
                  fontSize: "2.0rem",
                  color: "#fff",
                  lineHeight: 1.2,
                  textAlign: "center",
                  fontFamily: "countach, authority-italic, sans-serif",
                }}
              >
                {namePart}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box 
          sx={{ 
            padding: 2,
            backgroundColor: "rgb(0, 17, 40)",
            backgroundImage: "linear-gradient(150deg, rgb(19, 36, 78) 0%, rgb(0, 0, 0) 100%)",
            backgroundBlendMode: "normal",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              color: "#ffffff",
            }}
          >
            Höchstbietender:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              wordBreak: "break-word",
              color: "#ffffff",
            }}
          >
            {currentHighestBidder ? currentHighestBidder : "Noch keine Gebote"}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              fontSize: "1.4rem",
              mb: 2,
              color: "#ffffff",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
            }}
          >
            Aktuelles Gebot: {formatCurrency(currentBid)}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontWeight: remainingTime <= 180000 ? "bold" : "normal",
              color: isExpired
                ? "#ff6b6b"
                : remainingTime <= 180000
                ? "#ffd93d"
                : "#ffffff",
            }}
          >
            {isExpired 
              ? "Auktion beendet" 
              : `Endet in: ${timeLeft}${remainingTime <= 180000 ? " (Letzte Minuten!)" : ""}`
            }
          </Typography>

          <Box sx={{ mt: "auto" }}>{renderActionButton()}</Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TrikotCard;
